import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Box, Button, Collapse, IconButton,
  List, ListItem, ListItemIcon, ListItemText, Chip, Divider, CircularProgress,
  Alert
} from '@mui/material';
import {
  AutoAwesome, Close, ExpandMore, ExpandLess, Settings, Refresh,
  Warning, Info, CheckCircle, TrendingUp, Security, Work,
  LocationOn, Star, AttachMoney, Speed
} from '@mui/icons-material';
import api from '../../services/api';
import UnifiedOnboardingModal from '../onboarding/UnifiedOnboardingModal';
import { status, text } from '../../theme/colors';

/**
 * Contractor Smart Insights
 * Uses rule-based logic to provide intelligent insights:
 * - Job matching based on profile and location
 * - Performance insights and recommendations
 * - Financial forecasts and trends
 * - Actionable suggestions
 */
const ContractorSmartAssistant = ({ onDismiss, onOpenSettings, contractorProfile, completionPercentage }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hiddenCount, setHiddenCount] = useState(0);

  // Fetch smart insights from backend
  useEffect(() => {
    fetchContractorInsights();
  }, []);

  const fetchContractorInsights = async (refresh = false) => {
    try {
      setLoading(true);
      setError(null);

      // Check if contractor has completed their profile before showing insights
      // If profile is incomplete, show onboarding-focused insights instead
      const hasBasicProfile = contractorProfile?.bio && contractorProfile?.city;
      if (!hasBasicProfile) {
        setInsights(generateOnboardingInsights());
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/smart-insights/contractor-dashboard', {
          params: refresh ? { refresh: 'true' } : {}
        });

        if (response.data.success) {
          setInsights(response.data.data.insights || []);
          setHiddenCount(response.data.data.hiddenCount || 0);
        }
      } catch (apiError) {
        // If API endpoint doesn't exist yet, silently show nothing
        console.log('API not available, no insights to show');
        setInsights([]);
      }
    } catch (err) {
      console.error('Error fetching contractor insights:', err);
      // Silently fail - insights are optional
      setInsights([]);
    } finally {
      setLoading(false);
    }
  };

  // Generate onboarding-focused insights for contractors with incomplete profiles
  const generateOnboardingInsights = () => {
    return [
      {
        type: 'profile_action',
        category: 'urgent',
        title: 'Complete Your Profile to Start Bidding',
        description: 'Add your bio, location, experience, specializations, and one verifiable portfolio project before submitting bids.',
        actionText: 'Complete Profile',
        actionRoute: '/profile',
        icon: 'warning',
        data: { completion: 0 }
      },
      {
        type: 'info',
        category: 'suggestion',
        title: 'Browse Available Projects',
        description: 'While you set up your profile, you can browse construction projects in your area.',
        actionText: 'Browse Projects',
        actionRoute: '/contractor/marketplace',
        icon: 'work',
        data: {}
      }
    ];
  };

  // Generate mock insights based on contractor profile
  const generateMockInsights = (profile) => {
    const mockInsights = [];

    // Job matching insights
    mockInsights.push({
      type: 'job_match',
      category: 'important',
      title: 'New Projects Match Your Profile',
      description: `3 new ${profile?.specialty || 'construction'} projects in ${profile?.location || 'your area'}`,
      actionText: 'View Projects',
      actionRoute: '/contractor/marketplace',
      icon: 'work',
      data: { count: 3, match_score: 95 }
    });

    // Performance insight
    mockInsights.push({
      type: 'performance',
      category: 'suggestion',
      title: 'Your Response Time is Excellent',
      description: 'You respond 30% faster than average contractors. Keep it up!',
      actionText: 'View Stats',
      actionRoute: '/contractor/profile',
      icon: 'speed',
      data: { improvement: 30 }
    });

    // Financial forecast
    mockInsights.push({
      type: 'financial_forecast',
      category: 'suggestion',
      title: 'Earnings Forecast',
      description: 'Based on your current projects, you\'re on track to earn $5,200 this month',
      actionText: 'View Finances',
      actionRoute: '/contractor/finances',
      icon: 'money',
      data: { forecast: 5200 }
    });

    // Profile completion
    if (!profile?.portfolio_complete) {
      mockInsights.push({
        type: 'profile_action',
        category: 'important',
        title: 'Add a Verifiable Portfolio Project',
        description: 'Add one completed public project with real details and a finished photo before submitting bids.',
        actionText: 'Add Portfolio',
        actionRoute: '/contractor/portfolio',
        icon: 'warning',
        data: { completion: 60 }
      });
    }

    return mockInsights;
  };

  // Track user interaction
  const trackInteraction = async (insight, action) => {
    try {
      await api.post('/smart-insights/track', {
        insightType: insight.type,
        insightCategory: insight.category,
        assistantType: 'contractor_dashboard',
        action: action,
        insightData: insight.data
      }).catch(() => {
        // Silently fail tracking
      });
    } catch (err) {
      // Silently fail tracking
    }
  };

  // Handle insight click
  const handleInsightClick = (insight) => {
    trackInteraction(insight, 'clicked');
    if (insight.actionRoute) {
      navigate(insight.actionRoute);
    }
  };

  // Handle dismiss
  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  // Get icon for insight
  const getInsightIcon = (iconName, category, type) => {
    const iconProps = { sx: { fontSize: 24 } };

    if (iconName === 'work') return <Work {...iconProps} sx={{ color: '#16a085' }} />;
    if (iconName === 'speed') return <Speed {...iconProps} sx={{ color: status.success }} />;
    if (iconName === 'money') return <AttachMoney {...iconProps} sx={{ color: status.warning }} />;
    if (iconName === 'star') return <Star {...iconProps} sx={{ color: status.warning }} />;
    if (iconName === 'location') return <LocationOn {...iconProps} sx={{ color: '#16a085' }} />;

    if (category === 'urgent' || iconName === 'warning') return <Warning {...iconProps} sx={{ color: status.errorDark }} />;
    if (category === 'important') return <Info {...iconProps} sx={{ color: status.warning }} />;
    if (type === 'fraud_alert') return <Security {...iconProps} sx={{ color: status.errorDark }} />;
    if (type.includes('prediction') || type.includes('forecast')) return <TrendingUp {...iconProps} sx={{ color: '#16a085' }} />;

    return <CheckCircle {...iconProps} sx={{ color: status.success }} />;
  };

  // Get color for insight
  const getInsightColor = (category, type) => {
    if (category === 'urgent' || type === 'fraud_alert') return status.errorDark;
    if (category === 'important') return status.warning;
    return '#16a085';
  };

  // Group insights by category
  const groupedInsights = {
    urgent: insights.filter(i => i.category === 'urgent'),
    important: insights.filter(i => i.category === 'important'),
    suggestion: insights.filter(i => i.category === 'suggestion')
  };

  const totalInsights = insights.length;

  if (dismissed) {
    return null;
  }

  if (loading) {
    return (
      <Card
        sx={{
          borderRadius: 2,
          mb: 3,
          border: '2px solid #16a085',
          bgcolor: '#16a08510',
          boxShadow: '0 4px 12px rgba(22, 160, 133, 0.15)'
        }}
      >
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={30} sx={{ color: '#16a085' }} />
          <Typography variant="body2" sx={{ ml: 2, color: text.secondary }}>
            Analyzing opportunities for you...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }} onClose={handleDismiss}>
        {error}
      </Alert>
    );
  }

  if (totalInsights === 0) {
    return null;
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        mb: 3,
        border: '2px solid #16a085',
        bgcolor: '#16a08510',
        boxShadow: '0 4px 12px rgba(22, 160, 133, 0.15)',
        transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome sx={{ color: '#16a085', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              💡 Smart Insights
            </Typography>
            <Chip
              label={`${totalInsights} insight${totalInsights > 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: '#16a085', color: 'white', fontWeight: 600 }}
            />
          </Box>
          <Box>
            <IconButton size="small" onClick={() => fetchContractorInsights(true)} title="Refresh insights" disabled={loading}>
              <Refresh sx={{ fontSize: 20 }} />
            </IconButton>
            {onOpenSettings && (
              <IconButton size="small" onClick={onOpenSettings}>
                <Settings />
              </IconButton>
            )}
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <IconButton size="small" onClick={handleDismiss}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <List sx={{ py: 0 }}>
            {/* Urgent insights */}
            {groupedInsights.urgent.map((insight, index) => (
              <React.Fragment key={`urgent-${index}`}>
                <ListItem
                  button
                  onClick={() => handleInsightClick(insight)}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: `${status.errorDark}10`,
                    border: '1px solid #e74c3c30',
                    '&:hover': {
                      bgcolor: `${status.errorDark}20`,
                      transform: 'translateX(4px)',
                      transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }
                  }}
                >
                  <ListItemIcon>
                    {getInsightIcon(insight.icon, insight.category, insight.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: status.errorDark }}>
                        {insight.title}
                      </Typography>
                    }
                    secondary={insight.description}
                  />
                  {insight.actionText && (
                    <Button
                      size="small"
                      sx={{
                        color: status.errorDark,
                        fontWeight: 600,
                        '&:hover': { bgcolor: `${status.errorDark}20` }
                      }}
                    >
                      {insight.actionText} →
                    </Button>
                  )}
                </ListItem>
              </React.Fragment>
            ))}

            {/* Important insights */}
            {groupedInsights.important.map((insight, index) => (
              <React.Fragment key={`important-${index}`}>
                <ListItem
                  button
                  onClick={() => handleInsightClick(insight)}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: `${status.warning}10`,
                    border: '1px solid #FAA61A30',
                    '&:hover': {
                      bgcolor: `${status.warning}20`,
                      transform: 'translateX(4px)',
                      transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }
                  }}
                >
                  <ListItemIcon>
                    {getInsightIcon(insight.icon, insight.category, insight.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: status.warning }}>
                        {insight.title}
                      </Typography>
                    }
                    secondary={insight.description}
                  />
                  {insight.actionText && (
                    <Button
                      size="small"
                      sx={{
                        color: status.warning,
                        fontWeight: 600,
                        '&:hover': { bgcolor: `${status.warning}20` }
                      }}
                    >
                      {insight.actionText} →
                    </Button>
                  )}
                </ListItem>
              </React.Fragment>
            ))}

            {/* Suggestion insights */}
            {groupedInsights.suggestion.map((insight, index) => (
              <React.Fragment key={`suggestion-${index}`}>
                <ListItem
                  button
                  onClick={() => handleInsightClick(insight)}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: '#16a08510',
                    border: '1px solid #16a08530',
                    '&:hover': {
                      bgcolor: '#16a08520',
                      transform: 'translateX(4px)',
                      transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }
                  }}
                >
                  <ListItemIcon>
                    {getInsightIcon(insight.icon, insight.category, insight.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#16a085' }}>
                        {insight.title}
                      </Typography>
                    }
                    secondary={insight.description}
                  />
                  {insight.actionText && (
                    <Button
                      size="small"
                      sx={{
                        color: '#16a085',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#16a08520' }
                      }}
                    >
                      {insight.actionText} →
                    </Button>
                  )}
                </ListItem>
              </React.Fragment>
            ))}
          </List>

          {hiddenCount > 0 && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="caption" sx={{ color: text.secondary }}>
                {hiddenCount} more insight{hiddenCount > 1 ? 's' : ''} hidden
              </Typography>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ContractorSmartAssistant;
